import { useState, useEffect, Suspense, lazy } from 'react'
import {
  createHashRouter,
  RouterProvider,
  Navigate,
  Routes,
  Route,
} from 'react-router-dom'
import { Divider } from 'semantic-ui-react'
import jwtDecode from 'jwt-decode'
import { ThemeProvider } from 'styled-components'
import { ApolloProvider } from '@apollo/client'
import { LoadingSpinner, Form, Content } from '@/ui'
import { useBreakpoints } from '@/hooks'
import AuthRoute from '@/components/AuthRoute'
import logout from '@/helpers/logout'
import customFetch from '@/helpers/custom-fetch'
import config from '@/config'
import ThemeProviderWrapper from './ThemeProviderWrapper'
import { client } from './client'
import i18n from './i18n'
import Helpkit from './Helpkit'
import EmployeeSidebarPlaceholder from '@/pages/Employee/EmployeeSidebarPlaceholder'
import EmployeeSidebarPlaceholderMobile from '@/pages/Employee/EmployeeSidebarPlaceholderMobile'
import { SidebarMenu } from '@/pages/Employee/Styles'
import { SYSTEM_FIELDS_QUERY } from '@/graphql/queries/SystemField'
import { standalonePages } from '@/const/standalonePages'

const Wrapper = ({ unauthenticated, children }) => (
  <ThemeProviderWrapper unauthenticated={unauthenticated}>
    {children}
  </ThemeProviderWrapper>
)

const AddEmployee = lazy(() => import('@/pages/AddEmployee'))
const Competences = lazy(() => import('@/pages/Competences'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Documents = lazy(() => import('@/pages/Documents'))
const Employee = lazy(() => import('@/pages/Employee'))
const Employees = lazy(() => import('@/pages/Employees'))
const EngagementBuilder = lazy(() => import('@/pages/Preboardings'))
const Pulse = lazy(() => import('@/pages/Pulse'))
const Equipment = lazy(() => import('@/pages/Equipment'))
const Information = lazy(() => import('@/pages/Information'))
const Login = lazy(() => import('@/pages/Login/index'))
const PreboardingForm = lazy(() => import('@/pages/PreboardingForm'))
const PreboardingLogin = lazy(() => import('@/pages/PreboardingLogin'))
const Processes = lazy(() => import('@/pages/Processes'))
const Settings = lazy(() => import('@/pages/Settings'))
const SignDocuments = lazy(() => import('@/pages/SignDocuments'))
const SignLogin = lazy(() => import('@/pages/SignLogin'))
const Vacation = lazy(() => import('@/pages/Vacation'))
const Approvals = lazy(() => import('@/pages/Approvals'))
const PulseSurveyLogin = lazy(() => import('@/pages/PulseSurveyLogin'))
const SubmitPulseSurveyResponse = lazy(() =>
  import('@/pages/SubmitPulseSurveyResponse'),
)
const TimeRegistration = lazy(() => import('@/pages/TimeRegistration'))

const createLoginRedirectSearch = () => {
  if (!sessionStorage.getItem('redirectUrl') && window.location.hash !== '#/') {
    sessionStorage.setItem('redirectUrl', window.location.hash)
  }

  if (sessionStorage.getItem('redirectUrl')) {
    return `redirectUrl=${window.location.hash.replace(
      '#',
      '',
    )}${getEngagementQueryParams(true)}`
  }

  return getEngagementQueryParams()
}

/**
 *
 * @param {boolean} prependAmpersand Indicates whether to strip '?'
 * from queryparam and prepend '&' instead
 */
const getEngagementQueryParams = (prependAmpersand = false) => {
  const url = window.location.href
  const queryParamIndex = prependAmpersand
    ? url.indexOf('?') + 1
    : url.indexOf('?')
  return queryParamIndex > 0
    ? `${prependAmpersand ? '&' : ''}${url.substring(queryParamIndex)}`
    : ''
}

// The employee routes has to be extracted in to a separate component,
// because useBreakpoints is dependant on the theme being initialized
export const EmployeeRoutes = () => {
  const { isMobile } = useBreakpoints()

  const fallback = (
    <div>
      {isMobile ? (
        <div>
          <Form>
            <Form.Select />
          </Form>

          <Divider style={{ margin: '24px 0' }} />

          <EmployeeSidebarPlaceholderMobile />
        </div>
      ) : (
        <SidebarMenu>
          <EmployeeSidebarPlaceholder />
        </SidebarMenu>
      )}

      <Content allowOverflow withSubMenu>
        <LoadingSpinner />
      </Content>
    </div>
  )

  return (
    <Suspense fallback={fallback}>
      <Routes>
        <Route path="/:id/*" element={<Employee />} />
      </Routes>
    </Suspense>
  )
}

const theme = {
  fontFamily: 'Roboto',
  primaryColor: '#0398cc',
  secondaryColor: '#ff9a2f',
  primaryFontColor: '#000',
  secondaryFontColor: '#fff',
  backgroundColor: '#eff3f9',
  colors: {
    red: '#db2828',
    green: '#21ba45',
    grey: '#4d4e4f',
  },
}

const StaffRoutes = () => {
  const [loaded, setLoaded] = useState(false)
  const [themeLoaded, setThemeLoaded] = useState(false)

  const isAuthed =
    // Since you can be authed as a different user on a standalone page, we need to make sure the session is not overwritten.
    !standalonePages.some(path => window.location.href.includes(path)) &&
    !!document.cookie
      ?.split(';')
      ?.find(key => key.trim().startsWith('access_token='))
      ?.split('=')?.[1]

  const showHelpkit = !['/login/reset-password', ...standalonePages].some(
    path => window.location.href.includes(path),
  )

  const setLocale = async locale => {
    const newLocale = ['en_GB', 'nn_NO', 'nb_NO'].includes(locale)
      ? locale
      : locale?.substring(0, 2)

    await i18n({
      lng: newLocale,
    })
    window.__localeId__ = newLocale
  }

  useEffect(() => {
    const init = async () => {
      if (window.location.href.includes('/redirect/zenegy')) {
        const zenegyIntegrationId = localStorage.getItem('zenegyIntegrationId')
        if (zenegyIntegrationId && zenegyIntegrationId !== 'null') {
          window.location.href = `${window.location.origin}/#/settings/externalIntegration/activateExternalIntegration/zenegy/${zenegyIntegrationId}${window.location.search}`
        } else {
          window.location.href = `${window.location.origin}/#/settings/externalIntegration/activateExternalIntegration/zenegy${window.location.search}`
        }
        return
      }

      if (window.location.href.includes('/redirect/integration')) {
        const integrationId = localStorage.getItem('integrationId')
        localStorage.setItem('integrationCreation', null)

        if (window.location.search.substring(0, 19) === '?authorizationCode=') {
          window.location.href = `${window.location.origin}/#/settings/externalIntegration/${integrationId}${window.location.search}`
        } else if (window.location.search.substring(0, 6) === '?final') {
          window.location.href = `${window.location.origin}/#/settings/externalIntegration/${integrationId}${window.location.search}`
        } else {
          window.location.href = `${window.location.origin}/#/settings/externalIntegration/${integrationId}?final=false`
        }
        return
      }

      if (!isAuthed) {
        await setLocale(navigator?.language)
        setLoaded(true)
        return
      }

      // Check validity of tokens before loading application.
      // If invalid, the request will fail and redirect to login.
      const response = await customFetch(`${config.endpoints.auth}/token`)
      if (!response?.ok) {
        logout()
        return
      }

      const { accessToken, userScope, error } = await response.json()
      if (error) {
        logout()
        return
      }

      if (accessToken && userScope) {
        sessionStorage.setItem('userScope', userScope)
        sessionStorage.setItem('accessToken', accessToken)
        const { user } = jwtDecode(accessToken ?? '') ?? {}
        await setLocale(user.locale)
      }

      // prefetch system fields
      await client.query({
        query: SYSTEM_FIELDS_QUERY,
        variables: {
          getUserData: false,
          userIds: [],
        },
      })

      setLoaded(true)
    }

    init()
  }, [isAuthed])

  if (!loaded || !themeLoaded) {
    return (
      <ApolloProvider client={client}>
        <ThemeProviderWrapper
          preloading
          unauthenticated
          onFinishedLoading={() => setThemeLoaded(true)}>
          {isAuthed && <LoadingSpinner defaultColor />}
        </ThemeProviderWrapper>
      </ApolloProvider>
    )
  }

  if (!isAuthed) {
    return (
      <div>
        <ApolloProvider client={client}>
          <ThemeProvider theme={theme}>
            <Wrapper unauthenticated>
              <Suspense fallback={<div />}>
                <Routes>
                  <Route path="/login/*" element={<Login />} />
                  <Route path="/sign-login" element={<SignLogin />} />
                  <Route
                    path="/sign-documents/:id"
                    element={<SignDocuments />}
                  />
                  <Route
                    path="/engagement-login"
                    element={<PreboardingLogin />}
                  />
                  <Route
                    path="/engagement-form/:id"
                    element={<PreboardingForm />}
                  />
                  <Route
                    path="/pulse-survey-login"
                    element={<PulseSurveyLogin />}
                  />
                  <Route
                    path="/submit-pulse-survey/:id"
                    element={<SubmitPulseSurveyResponse />}
                  />

                  <Route
                    path="/appbuilder-login"
                    element={
                      <Navigate
                        replace
                        to={{
                          pathname: '/engagement-login',
                          search: getEngagementQueryParams(),
                        }}
                      />
                    }
                  />

                  <Route
                    path="*"
                    element={
                      <Navigate
                        replace
                        to={{
                          pathname: '/login',
                          search: createLoginRedirectSearch(),
                        }}
                      />
                    }
                  />
                </Routes>
              </Suspense>
            </Wrapper>
          </ThemeProvider>
        </ApolloProvider>
      </div>
    )
  }

  return (
    <ApolloProvider client={client}>
      <Wrapper>
        <Suspense fallback={<div />}>
          <Routes>
            <Route path="/dashboard/*" element={<Dashboard />} />
            <Route
              path="/vacation"
              element={
                <AuthRoute
                  permissions={[
                    'vacation:type:write:all',
                    'vacation:assign:read:department',
                    'vacation:assign:read:all',
                    'vacation:assign_bytype:read:department',
                    'vacation:assign_bytype:read:all',
                  ]}>
                  <Vacation />
                </AuthRoute>
              }
            />
            <Route
              path="/documents/*"
              element={
                <AuthRoute
                  permissions={[
                    'document_folder:department:read:department',
                    'document_folder:department:read:all',
                    'document_folder:company:read:all',
                    'document_folder:blueprint:read:all',
                    'document_folder:blueprint:write:all',
                    'document:document_type:read:all',
                    'document:document_type:write:all',
                    'document:template:read:self',
                    'document:template:read:department',
                    'document:template:read:all',
                  ]}>
                  <Documents />
                </AuthRoute>
              }
            />
            <Route
              path="/equipment/*"
              element={
                <AuthRoute
                  permissions={[
                    'equipment:general:read:all',
                    'equipment:general:read:department',
                    'equipment:bytype:read:all',
                    'equipment:bytype:read:department',
                  ]}>
                  <Equipment />
                </AuthRoute>
              }
            />
            <Route
              path="/competences/*"
              element={
                <AuthRoute
                  permissions={[
                    'competence:general:read:all',
                    'competence:assign:read:department',
                    'competence:assign:read:all',
                  ]}>
                  <Competences />
                </AuthRoute>
              }
            />
            <Route
              path="/processes/*"
              element={
                <AuthRoute
                  permissions={[
                    'process:general:read:self',
                    'process:general:read:department',
                    'process:general:read:all',
                    'process:template:read:department',
                    'process:template:read:all',
                    'process:bytype:read:department',
                    'process:bytype:read:all',
                    'process:process_type:write:all',
                  ]}>
                  <Processes />
                </AuthRoute>
              }
            />
            <Route
              path="/engagementbuilder/*"
              element={
                <AuthRoute
                  permissions={[
                    'app_builder:general:read:self',
                    'app_builder:general:read:department',
                    'app_builder:general:read:all',
                    'app_builder:templates:read:department',
                    'app_builder:templates:read:all',
                    'app_builder:bytype:read:self',
                    'app_builder:bytype:read:department',
                    'app_builder:bytype:read:all',
                    'app_builder:templates_bytype:read:department',
                    'app_builder:templates_bytype:read:all',
                  ]}>
                  <EngagementBuilder />
                </AuthRoute>
              }
            />
            <Route
              path="pulse/*"
              element={
                <AuthRoute>
                  <Pulse />
                </AuthRoute>
              }
            />
            <Route path="/information/*" element={<Information />} />
            <Route
              path="settings/*"
              element={
                <AuthRoute>
                  <Settings />
                </AuthRoute>
              }
            />
            <Route
              path="approvals/*"
              element={
                <AuthRoute>
                  <Approvals />
                </AuthRoute>
              }
            />
            <Route path="/login/reset-password/*" element={<Login />} />
            <Route path="/sign-login" element={<SignLogin />} />
            <Route path="/sign-documents/:id" element={<SignDocuments />} />
            <Route path="/engagement-login" element={<PreboardingLogin />} />
            <Route path="/engagement-form/:id" element={<PreboardingForm />} />
            <Route path="/pulse-survey-login" element={<PulseSurveyLogin />} />
            <Route
              path="/submit-pulse-survey/:id"
              element={<SubmitPulseSurveyResponse />}
            />
            <Route path="/time-registration/*" element={<TimeRegistration />} />

            <Route
              path="/appbuilder-login"
              element={
                <Navigate
                  replace
                  to={{
                    pathname: '/engagement-login',
                    search: getEngagementQueryParams(),
                  }}
                />
              }
            />

            <Route
              path="/login"
              element={<Navigate replace to="/dashboard" />}
            />
            <Route path="/" element={<Navigate replace to="/dashboard" />} />
            <Route path="/employees/addemployee" element={<AddEmployee />} />
            <Route path="/employee/*" element={<EmployeeRoutes />} />
            <Route path="/employees/*" element={<Employees />} />
          </Routes>
          {showHelpkit && <Helpkit />}
        </Suspense>
      </Wrapper>
    </ApolloProvider>
  )
}

const router = createHashRouter([{ path: '*', Component: StaffRoutes }])

export default function RoutesWrapper() {
  return <RouterProvider router={router} />
}
